<script setup lang="ts">
import ChannelIcon from '@/components/channels/ChannelIcon.vue'
import BadgedAvatar from '@/components/common/avatar/BadgedAvatar.vue'
import CommunityButton from '@/components/common/buttons/CommunityButton.vue'
import CommunityBottomSheet from '@/components/common/CommunityBottomSheet.vue'
import { GroupState } from '@/composition/groups'
import { MobileScrollContainer } from '@ghl/clientportal-core'
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  EyeIcon,
} from '@gohighlevel/ghl-icons/24/outline'
import {
  UITextLgMedium,
  UITextMdMedium,
  UITextSmMedium,
  UITextSmNormal,
  UITextXsRegular,
} from '@gohighlevel/ghl-ui'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

const props = defineProps({
  onBack: {
    type: Function,
    required: true,
  },
  formData: {
    type: Object,
    required: true,
  },
  updateFormData: {
    type: Function,
    required: false,
  },
  isEditMode: {
    type: Boolean,
    required: false,
    default: false,
  },
  participantCount: {
    type: Number,
    required: false,
    default: 0,
  },
  isLive: {
    type: Boolean,
    required: false,
    default: false,
  },
  handleFlipCamera: {
    type: Function,
    required: false,
  },
  class: {
    type: String,
    required: false,
    default: '',
  },
  user: {
    type: Object,
    required: false,
  },
  title: {
    type: String,
    required: false,
  },
})

const route = useRoute()
const isChannelSelectable = ref(true)
const openChanneModal = ref(false)
const currentSelectedChannel = ref(props.formData.selectedChannelId)
const { t } = useI18n()

const selectedChannel = computed(() => {
  return GroupState.state.nonRestrictedChannelList.find(
    (channel: any) => channel._id === props.formData.selectedChannelId
  )
})

const handleChannelClick = () => {
  openChanneModal.value = true
}

const channels = computed(() => {
  // Don't allow to select announcement channels
  if (props.isEditMode) {
    return []
  }
  const nonRestrictedChannelList = GroupState.state.nonRestrictedChannelList
  const filteredChannels = nonRestrictedChannelList.map((channel: any) => {
    if (
      route.params?.channelSlug &&
      channel.slug === route.params.channelSlug
    ) {
      // Select current selected channel, and don't allow to select other channels

      props.updateFormData?.('selectedChannelId', channel._id)
      currentSelectedChannel.value = channel._id
      isChannelSelectable.value = false
    }
    return {
      label: channel.name,
      value: channel._id,
      ...channel,
    }
  })
  // if at home select first channel
  if (!props.formData.selectedChannelId && filteredChannels.length > 0) {
    props.updateFormData?.('selectedChannelId', filteredChannels[0].value)
    currentSelectedChannel.value = filteredChannels[0].value
  }
  return filteredChannels
})

const handleChannelSelect = (channel: any) => {
  currentSelectedChannel.value = channel.value
}
const changeChannel = () => {
  props.updateFormData?.('selectedChannelId', currentSelectedChannel.value)
  openChanneModal.value = false
}

const goBack = () => {
  props.onBack()
}
</script>

<template>
  <div
    :class="[
      'bg-communities-background/90 top-bar-container  z-10 z-40 box-content flex h-[40px] items-center  justify-between bg-black p-4',
      props.class,
    ]"
    @click.stop
  >
    <div class="flex items-center gap-3">
      <div
        v-if="!isEditMode"
        role="button"
        tabindex="0"
        class="cursor-pointer"
        @click="goBack"
        @keyup.enter="goBack"
      >
        <ChevronLeftIcon class="h-6 w-6 text-white" />
      </div>
      <BadgedAvatar
        :avatar-url="user?.avatar"
        :full-name="user?.fullName"
        :show-badge="true"
        :show-online-status="true"
        size="large"
      />
      <div class="flex w-full flex-col">
        <UITextMdMedium class="user-name-text w-[150px] truncate text-white">
          {{ user?.fullName }}
        </UITextMdMedium>
        <div
          v-if="!isEditMode"
          class="flex items-center gap-2"
          role="button"
          tabindex="0"
          :disabled="isEditMode"
          @click="handleChannelClick"
          @keyup.enter="handleChannelClick"
        >
          <UITextSmNormal class="text-white">posting in</UITextSmNormal>
          <div class="flex items-center gap-2 rounded-md bg-white px-2 py-1">
            <ChannelIcon
              v-if="selectedChannel"
              :channel="selectedChannel"
              :icon-size="12"
              :emoji-size="12"
              :show-lock-icon="false"
            />
            <UITextSmNormal>{{ selectedChannel?.name }}</UITextSmNormal>
            <ChevronDownIcon class="h-4 w-4" />
          </div>
        </div>
        <UITextXsRegular
          v-if="title"
          class="line-clamp-1 w-[150px] text-[10px] text-white text-white"
          >{{ title }}</UITextXsRegular
        >
      </div>
    </div>
    <div class="z-10 mr-2 flex items-center gap-2">
      <div
        v-if="isEditMode && isLive"
        class="flex items-center gap-2 rounded-md bg-white px-2 py-1"
      >
        <div class="h-2 w-2 animate-pulse rounded-full bg-red-500" />
        <UITextSmMedium>Live</UITextSmMedium>
      </div>
      <div
        v-if="isEditMode && participantCount > 0"
        class="flex items-center gap-2 rounded-md bg-white px-2 py-1"
      >
        <EyeIcon class="h-4 w-4" />
        <UITextSmMedium>{{ participantCount }}</UITextSmMedium>
      </div>
    </div>
  </div>
  <CommunityBottomSheet
    v-model="openChanneModal"
    :is-full-screen="false"
    :is-drag-handle="true"
    :is-scroll-lock="true"
    :snap-point="'450px'"
    :is-backdrop="true"
    :is-persistent="true"
  >
    <div class="px-2 pb-4 text-black">
      <UITextLgMedium class="text-center text-communities-font-primary">
        {{ t('golive.selectChannel') }}
      </UITextLgMedium>
      <MobileScrollContainer
        id="side-bar"
        class="sticky mb-2 w-full overflow-y-auto rounded-xl bg-transparent text-communities-primary"
        :style="{ height: '300px' }"
      >
        <template #scroll-slot>
          <div
            v-for="channel in channels"
            :key="channel.value"
            class="channel-card m-2 my-4 flex items-center justify-between gap-2 border border-solid border-communities-border px-4 py-4"
            :class="
              currentSelectedChannel === channel.value
                ? 'bg-communities-surface'
                : ''
            "
            tabindex="0"
            role="button"
            @click="handleChannelSelect(channel)"
            @keyup.enter="handleChannelSelect(channel)"
          >
            <div class="flex items-center gap-2">
              <ChannelIcon
                :channel="channel"
                :icon-size="24"
                :emoji-size="24"
                :show-lock-icon="false"
              />
              <UITextSmMedium>
                {{ channel.label }}
              </UITextSmMedium>
            </div>
            <!-- Custom checkbox icon -->
            <div>
              <div
                class="check-icon"
                :class="{
                  'check-icon-active': channel.value === currentSelectedChannel,
                }"
              >
                <CheckIcon class="font-white h-4 w-4" />
              </div>
            </div>
          </div>
        </template>
      </MobileScrollContainer>
      <CommunityButton
        :id="`save-post-details-btn`"
        type="primary"
        size="medium"
        class="mt-2 w-full"
        @click="changeChannel"
      >
        {{ t('common.done') }}
      </CommunityButton>
    </div>
  </CommunityBottomSheet>
</template>
<style scoped>
.community-bottom-sheet {
  z-index: 200 !important;
}
.swipe-modal::backdrop {
  backdrop-filter: blur(1px) !important;
}
.channel-card {
  border-radius: 16px;
}
:deep(.n-checkbox-box__border) {
  border-radius: 50% !important;
}
:deep(.n-checkbox-box, .n-checkbox-box__border) {
  height: 24px !important;
  width: 24px !important;
}
:deep(
    .n-checkbox.n-checkbox--checked .n-checkbox-box,
    .n-checkbox.n-checkbox--indeterminate .n-checkbox-box
  ) {
  background-color: #000000 !important;
}
:deep(.check-icon) {
  color: #ffffff !important;
  fill: #ffffff !important;
}

/* custom checkbox icon */
.check-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid #101828;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease-in-out;
  transform: scale(1);
}

.check-icon-active {
  background-color: #101828;
  transform: scale(1.1);
  box-shadow: 0 0 0 4px rgba(16, 24, 40, 0.2);
  animation: checkPulse 0.6s ease-out;
}

@keyframes checkPulse {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(16, 24, 40, 0.4);
  }
  50% {
    transform: scale(1.15);
    box-shadow: 0 0 0 8px rgba(16, 24, 40, 0.2);
  }
  100% {
    transform: scale(1.1);
    box-shadow: 0 0 0 4px rgba(16, 24, 40, 0.2);
  }
}

.top-bar-container {
  animation: slideDownFromTop 0.3s ease-out;
}

@keyframes slideDownFromTop {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>
